import { Ellipsis, type LucideIcon } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import type { Brand, EnvInfo } from '../../api/types.ts';
import { cn } from '../../lib/cn.ts';
import { buttonTapped } from '../../lib/usage.ts';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu.tsx';
import { splitTabs, visibleItems } from './navItems.ts';

/**
 * The desktop list: the selected section is the one solid brand-coloured thing
 * in the chrome. The same on every page, so the bar never changes width.
 */
export function SideNav({ brand, env }: { brand: Brand; env: EnvInfo['env'] }) {
  return (
    <nav aria-label="Sections">
      <ul className="flex flex-col gap-1">
        {visibleItems(brand, env).map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex h-11 items-center gap-3 rounded-full px-4 text-[0.95rem] font-medium text-ink/75 transition-colors',
                  isActive ? 'bg-brand text-brand-foreground shadow-[0_6px_16px_-8px_var(--brand)]' : 'hover:bg-surface hover:text-ink',
                )
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function TabFace({ icon: Icon, label, active }: { icon: LucideIcon; label: string; active: boolean }) {
  return (
    <>
      <span className={cn('flex h-7 w-12 items-center justify-center rounded-full transition-colors', active && 'bg-brand-soft')}>
        <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.9} aria-hidden />
      </span>
      {label}
    </>
  );
}

const TAB_CLASS = 'flex w-full flex-col items-center gap-1 pt-2 pb-2 text-[0.7rem] font-medium';

/** The phone's tab bar, pinned to the bottom edge above the home indicator. */
export function TabBar({ brand, env }: { brand: Brand; env: EnvInfo['env'] }) {
  const { tabs, more } = splitTabs(visibleItems(brand, env));
  const location = useLocation();
  const navigate = useNavigate();
  const inMore = more.some((item) => location.pathname.startsWith(item.to));

  return (
    <nav
      aria-label="Sections"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
    >
      <ul className="mx-auto flex max-w-lg">
        {tabs.map(({ to, short, icon }) => (
          <li key={to} className="flex-1">
            <NavLink to={to} className={({ isActive }) => cn(TAB_CLASS, isActive ? 'text-brand' : 'text-muted')}>
              {({ isActive }) => <TabFace icon={icon} label={short} active={isActive} />}
            </NavLink>
          </li>
        ))}
        {more.length ? (
          <li className="flex-1">
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(TAB_CLASS, inMore ? 'text-brand' : 'text-muted')}
                aria-label="More sections"
                onClick={() => buttonTapped('more_sections')}
              >
                <TabFace icon={Ellipsis} label="More" active={inMore} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                {more.map(({ to, label, icon: Icon }) => (
                  <DropdownMenuItem key={to} onSelect={() => navigate(to)} className={cn(location.pathname.startsWith(to) && 'text-brand')}>
                    <Icon className="h-4 w-4" aria-hidden /> {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}
